import { IBaseFollowUser } from '@/models/Follow'
import { IGroup } from '@/models/Groups'
import { ISearch } from '@/models/search'
import { UserRoles } from '@/models/Users'
import { FollowService } from '@/service/FollowService'
import { GlobalProfileService } from '@/service/GlobalProfileService'
import { SearchService } from '@/service/searchService'
import { reactive } from 'vue'

export class IGlobalProfileState {
  state = reactive({
    groupOwned: { groups: [] as Partial<IGroup>[], total: 0 },
    groupMembership: { groups: [] as Partial<IGroup>[], total: 0 },
    contributions: {
      contributions: [] as ISearch[],
      total: 0,
      lastPaginatedId: '',
    },
    isMoreContributions: true,
    followingUsers: { users: [] as IBaseFollowUser[], isLoading: false },
    followersUsers: { users: [] as IBaseFollowUser[], isLoading: false },
  })

  async getUserOwnedGroups(
    contactId: string,
    role?: UserRoles
  ): Promise<{
    groups: Partial<IGroup>[]
    total: number
  }> {
    const response = await GlobalProfileService.getUserGroups({
      contactId,
      role,
    })
    this.state.groupOwned = {
      groups: response.groups,
      total: response.total,
    }
    return response
  }

  async getUserMembershipGroups(contactId: string): Promise<{
    groups: Partial<IGroup>[]
    total: number
  }> {
    const response = await GlobalProfileService.getUserGroups({
      contactId,
    })
    this.state.groupMembership = {
      groups: response.groups,
      total: response.total,
    }
    return response
  }

  async getUserAccessibleGroups(contactId: string): Promise<Partial<IGroup>[]> {
    const response = await GlobalProfileService.getUserAccessibleGroups(
      contactId
    )
    return response
  }

  async fetchContributions(
    contactId: string,
    contributionType?: string
  ): Promise<ISearch[]> {
    if (!this.state.isMoreContributions) {
      return this.state.contributions.contributions
    }
    const response = await SearchService.getUserContributions(
      contactId,
      this.state.contributions.lastPaginatedId ?? '',
      10,
      contributionType === 'post' ? 'post' : undefined
    )
    if (response.contributions.length === 0) {
      this.state.isMoreContributions = false
    }

    this.state.contributions = {
      contributions: [
        ...this.state.contributions.contributions,
        ...response.contributions,
      ],
      total: response.total,
      lastPaginatedId: response.lastPaginatedId,
    }
    return response.contributions
  }

  async fetchContributionsCount(contactId: string): Promise<number> {
    const response = await SearchService.getUserContributionsCount(contactId)
    return response
  }

  async fetchFollowingUsers(
    contactId: string,
    limit: number,
    pageNumber?: number
  ): Promise<void> {
    if (this.state.followingUsers.isLoading) {
      return
    }
    this.state.followingUsers.isLoading = true
    const response = await FollowService.getFollowingUsers(
      contactId,
      limit,
      pageNumber
    )
    this.state.followingUsers.users = response
    this.state.followingUsers.isLoading = false
  }

  async fetchFollowersUsers(
    contactId: string,
    limit: number,
    pageNumber?: number
  ): Promise<void> {
    if (this.state.followersUsers.isLoading) {
      return
    }
    this.state.followersUsers.isLoading = true
    const response = await FollowService.getFollowersUsers(
      contactId,
      limit,
      pageNumber
    )
    this.state.followersUsers.users = response
    this.state.followersUsers.isLoading = false
  }

  resetFollowersUsersState(): void {
    this.state.followersUsers = { users: [], isLoading: false }
  }

  resetFollowingUsersState(): void {
    this.state.followingUsers = { users: [], isLoading: false }
  }

  resetContributionsState(): void {
    this.state.contributions = {
      contributions: [],
      total: 0,
      lastPaginatedId: '',
    }
    this.state.isMoreContributions = true
  }
  resetGroupOwnedState(): void {
    this.state.groupOwned = { groups: [], total: 0 }
  }
  resetGroupMembershipState(): void {
    this.state.groupMembership = { groups: [], total: 0 }
  }
}

export const GlobalProfileState = new IGlobalProfileState()
